/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useMemo} from 'react';
import {useQuery, useQueryClient} from 'react-query';
import {loadJobSearchEngine} from '@ion/core/src/services/Connect';
import {DownloadJob} from '@ion/core/src/types/entities';
import {createKey, QueryKey} from '../config/QueryKeys';
import {
  createQueryResult,
  DurationResult,
  getStaleTime,
  onClientErrorHandler
} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type useJobResult = UseDefaultQueryResult & {
  job: DownloadJob;
  refresh: () => void;
  isFetching: boolean;
};

/**
 * The useJobId hook provides the Query for referencing the get Job information
 * by using job id.
 *
 * ```typescript
 *
 * import {useJobId} from '@ion/query/src/hooks/useJobId';
 *
 *  ...
 *
 *  const {job} = useJobId({
 *      query: 'jobId: 65677',
 *      ...
 * });
 *
 * ```
 *
 * @param   enabled when true, the query will be executed immediately. Defaults to
 *          false in order to ensure authentication has been completed prior to
 *          issuing the request.
 * @param   staleTime Allows for setting the staleTime for cached query data,
 *          defaults to 60 minutes (CTC session timeout duration).
 * @returns useJobId containing the query results and the jobs.
 */
export const useJobId = (
  params,
  enabled = true,
  keepPreviousData = false
): useJobResult => {
  const queryClient = useQueryClient();
  const key = useMemo(() => (
    createKey(QueryKey.LOAD_JOB_ID, params)
  ), [params]);
  const query = useQuery(key, async() => {
    try {
      const results = await loadJobSearchEngine(params);
      return results;
    } catch (err) {
      return onClientErrorHandler(err);
    }
  }, {
    ...getStaleTime(0) as DurationResult,
    enabled,
    keepPreviousData
  });

  const refresh = useCallback(() => {
    queryClient.invalidateQueries(query?.data);
  }, [query?.data]);

  return createQueryResult(query, {
    job        : query?.data?.items?.[0] || {},
    isFetching : query?.isFetching,
    refresh
  }) as useJobResult;
};

export default useJobId;
